import { useEffect, useId, useRef } from "react"
import { Modal } from "../ui/modal"
import { btnSecondary } from "./FormComponents"

export type ConfirmDialogProps = {
  isOpen: boolean
  title: string
  message: string
  confirmLabel?: string
  cancelLabel?: string
  /** Styles the confirm button as a destructive action. Default true. */
  danger?: boolean
  confirming?: boolean
  /** Texto del botón mientras `confirming` es true. Default "Eliminando...". */
  confirmingLabel?: string
  onConfirm: () => void
  onCancel: () => void
}

/**
 * Accessible replacement for `window.confirm()` on destructive row actions.
 * Cancel gets initial focus so Enter/click-through never confirms by
 * accident — the opposite default of the native confirm dialog.
 */
export function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmLabel = "Eliminar",
  cancelLabel = "Cancelar",
  danger = true,
  confirming = false,
  confirmingLabel = "Eliminando...",
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const titleId = `confirm-dialog-title-${useId()}`
  const cancelButtonRef = useRef<HTMLButtonElement>(null)

  // Se enfoca después de que Modal enfoca su propio contenedor (efecto del
  // hijo corre después que el del padre), ganando el foco inicial — así
  // Enter/Space nunca confirma por accidente, al revés del confirm() nativo.
  useEffect(() => {
    if (isOpen) cancelButtonRef.current?.focus()
  }, [isOpen])

  return (
    <Modal isOpen={isOpen} onClose={onCancel} showCloseButton={false} ariaLabelledBy={titleId} className="max-w-sm p-6">
      <h2 id={titleId} className="text-base font-semibold text-gray-900 dark:text-white">
        {title}
      </h2>
      <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{message}</p>
      <div className="mt-5 flex justify-end gap-2">
        <button ref={cancelButtonRef} type="button" className={`min-h-11 ${btnSecondary}`} onClick={onCancel} disabled={confirming}>
          {cancelLabel}
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={confirming}
          className={`min-h-11 rounded-xl px-4 py-2 text-sm font-medium text-white disabled:opacity-60 ${
            danger ? "bg-error-600 hover:bg-error-700" : "bg-brand-500 hover:bg-brand-600"
          }`}
        >
          {confirming ? confirmingLabel : confirmLabel}
        </button>
      </div>
    </Modal>
  )
}

export default ConfirmDialog
